/**
 * HierarchicalAttackPathReview — Step 5's view when `analysis_mode ===
 * 'hierarchical'`. Two outer tabs (Current / Child); Vector filter
 * sits inside each tab via the FilterDrawer.
 *
 * Mirror of step4-threats/HierarchicalThreatReview.tsx.
 */
import React, { useMemo, useState } from 'react';
import TableTabNav from '../../components/TableTabNav/TableTabNav';
import AttackPathTable from './AttackPathTable';
import ChildAttackPathsTable, { type ChildAttackPathView } from './ChildAttackPathsTable';
import type { AttackPathRecord } from './attackPathFieldSchema';
import type { HierarchicalModifications } from '../stepBodyProps';

const TAB_CURRENT = 'current';
const TAB_CHILD = 'child';

export interface HierarchicalAttackPathReviewProps {
  newPaths: AttackPathRecord[];
  childPaths: ChildAttackPathView[];
  editable: boolean;
  onAdd: () => void;
  onEdit: (path: AttackPathRecord) => void;
  onDelete: (path: AttackPathRecord) => void;
  /** Export + import slots — only wired into the Current TARA tab. */
  onExport?: () => void;
  onImport?: (file: File) => void;
  modifications: HierarchicalModifications;
  onToggleRelevance: (attackPathId: string, isRelevant: boolean) => void;
  onJustificationChange: (attackPathId: string, justification: string) => void;
  childInputsDisabled?: boolean;
  /** Persist child modifications on Done (Story 15.F). */
  onSaveChildModifications?: () => Promise<void> | void;
}

export default function HierarchicalAttackPathReview({
  newPaths,
  childPaths,
  editable,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  modifications,
  onToggleRelevance,
  onJustificationChange,
  childInputsDisabled,
  onSaveChildModifications,
}: HierarchicalAttackPathReviewProps) {
  const [activeTab, setActiveTab] = useState<string>(TAB_CURRENT);

  const childView: ChildAttackPathView[] = useMemo(
    () =>
      childPaths.map((c) => ({
        ...c,
        is_relevant: modifications[c.attack_path_id]?.is_relevant ?? c.is_relevant ?? true,
        relevance_justification:
          modifications[c.attack_path_id]?.relevance_justification ?? c.relevance_justification ?? '',
      })),
    [childPaths, modifications],
  );

  const handleToggle = (attackPathId: string) => {
    const current = childView.find((c) => c.attack_path_id === attackPathId);
    onToggleRelevance(attackPathId, !(current?.is_relevant !== false));
  };

  const tabs = [
    { key: TAB_CURRENT, label: `Current TARA (${newPaths.length})` },
    { key: TAB_CHILD, label: `Child TARAs (${childPaths.length})` },
  ];

  return (
    <>
      <TableTabNav
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Hierarchical attack path review tabs"
        bleed={false}
        sticky={false}
      />

      {activeTab === TAB_CURRENT ? (
        <AttackPathTable
          paths={newPaths}
          editable={editable}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onExport={onExport}
          onImport={onImport}
          emptyMessage="No new attack paths yet for this TARA."
        />
      ) : (
        <ChildAttackPathsTable
          paths={childView}
          onToggleRelevance={handleToggle}
          onJustificationChange={onJustificationChange}
          disabled={childInputsDisabled}
          onSave={onSaveChildModifications}
        />
      )}
    </>
  );
}
